import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { State } from '../../services/global-state';
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { Bucket } from '../../models/bucket';
import { PayrazrService } from '../../services/payrazr';
import { ValidationHelpers } from '../../services/validation-helpers';
import { Portfolio } from '../../models/portfolio';
import { Customer } from '../../models/customer';
import { HpfConsumerService, AddCustomersToConsumerUserRequest } from '../../services/hpf-consumer.service';

@autoinject
export class CustomerAccountLookupForm {

	validationController: ValidationController;
	validationErrors: any = [];

	requireCustomerAccountNumber: boolean;
	requireRegistrationId: boolean;
	requireRegistrationId2: boolean;
	requireRegistrationId3: boolean;
	requireZipCode: boolean;

	labelRegistrationId: string;
	labelRegistrationId2: string;
	labelRegistrationId3: string;
	labelZipCode: string;

	tooltipRegistrationId: string;
	tooltipRegistrationId2: string;
	tooltipRegistrationId3: string;
	tooltipZipCode: string;

	valueAccountNumber: string;
	valueBucket: Bucket;
	valueRegistrationId: string;
	valueRegistrationId2: string;
	valueRegistrationId3: string;
	valueZipCode: string;

	customerAccountNumberSettings: any;

	newCustomer: any;

	constructor(
		private globalState: State,
		private i18n: I18N,
		private router: Router,
		private payrazrService: PayrazrService,
		private consumerService: HpfConsumerService,
		validationControllerFactory: ValidationControllerFactory) {
		this.validationController = validationControllerFactory.createForCurrentScope();

		this.requireCustomerAccountNumber = this.globalState.business.portalSettings.settings.accountNumberSettings[0].required;

		this.customerAccountNumberSettings = this.globalState.business.portalSettings.accountNumberSettings;

		this.requireRegistrationId = this.globalState.business.portalSettings.registrationIdSettings.required;

		this.labelRegistrationId = this.globalState.business.portalSettings.ezpay.registrationIdSettings.label ?
			this.globalState.business.portalSettings.ezpay.registrationIdSettings.label :
			this.i18n.i18next.t('registrationId', 'Registration ID');

		this.tooltipRegistrationId = this.globalState.business.portalSettings.registrationIdSettings.tooltip ?
			this.globalState.business.portalSettings.registrationIdSettings.tooltip :
			'';

		this.requireRegistrationId2 = this.globalState.business.portalSettings.registrationId2Settings.required;

		this.labelRegistrationId2 = this.globalState.business.portalSettings.ezpay.registrationId2Settings.label ?
			this.globalState.business.portalSettings.ezpay.registrationId2Settings.label :
			this.i18n.i18next.t('registrationIdTwo', 'Registration ID 2');

		this.tooltipRegistrationId2 = this.globalState.business.portalSettings.registrationId2Settings.tooltip ?
			this.globalState.business.portalSettings.registrationId2Settings.tooltip :
			'';

		this.requireRegistrationId3 = this.globalState.business.portalSettings.registrationId3Settings.required;

		this.labelRegistrationId3 = this.globalState.business.portalSettings.ezpay.registrationId3Settings.label ?
			this.globalState.business.portalSettings.ezpay.registrationId3Settings.label :
			this.i18n.i18next.t('registrationIdThree', 'Registration ID 3');

		this.tooltipRegistrationId3 = this.globalState.business.portalSettings.registrationId3Settings.tooltip ?
			this.globalState.business.portalSettings.registrationId3Settings.tooltip :
			'';

		this.requireZipCode = this.globalState.business.portalSettings.zipcodeSettings.required;

		this.labelZipCode = this.globalState.business.portalSettings.ezpay.zipcodeSettings.label ?
			this.globalState.business.portalSettings.ezpay.zipcodeSettings.label :
			this.i18n.i18next.t('zipCode', 'Zip Code');

		this.tooltipZipCode = this.globalState.business.portalSettings.zipcodeSettings.tooltip ?
			this.globalState.business.portalSettings.zipcodeSettings.tooltip :
			'';

		this.configureValidationRules();
	}

	submit() {
		this.validationController.validate()
			.then(response => {
				if (!response.valid) {
					this.validationErrors = [];

					response.results.forEach(x => {
						if (!x.valid) {
							this.validationErrors.push(x);
						}
					});

					throw new Error('Validation Errors');
				}

				let bucketKey = this.valueBucket ? this.valueBucket.bucketKey : null;
				this.validationErrors = [];

				return this.payrazrService.lookupCustomerByFilters(
					this.valueAccountNumber,
					this.valueRegistrationId,
					this.valueRegistrationId2,
					this.valueRegistrationId3,
					this.valueZipCode,
					bucketKey);
			})
			.then(response => {
				if (this.handleCustomerLookupSuccess(response)) {
					return response;
				} else {
					throw new Error('Other Customer Lookup Response Errors');
				}
			})
			.then(response => {
				// Persist new customer association.
				let request = new AddCustomersToConsumerUserRequest();
				request.customerKeys = [this.newCustomer.customerKey];
				return this.consumerService.addCustomersToConsumerUser(request);
			})
			.then(response => {
				if (!response.isSuccessful) {
					throw new Error('An error has occurred.');
				}

				this.associateNewCustomer();
			})
			.then(() => {
				// Notification Preferences
				this.goToNotificationPreferences();
			})
			.catch(reason => {
				if (reason.message === 'Validation Errors'
					|| reason.message === 'Other Customer Lookup Response Errors') {
					return;
				}

				this.handleErrors(reason);
			});
	}

	goToNotificationPreferences() {
		this.router.navigateToRoute('addCustomerNotificationPreferences');
	}

	associateNewCustomer() {
		if (this.globalState.associatedCustomers.length === 0) {
			this.globalState.associatedCustomers = [new Customer(this.globalState.business, this.newCustomer)];
		} else {
			this.globalState.associatedCustomers.push(new Customer(this.globalState.business, this.newCustomer));
		}
	}

	handleCustomerLookupSuccess(response: any): boolean {
		this.newCustomer = response.entity;

		if (this.newCustomer == null) {
			if (response.message === 'customerAccountNotEligibleForPayments') {
				if (!this.globalState.business.portalSettings.csPhone) {
					this.validationErrors.push(this.i18n.i18next.t('customerAccountNotEligibleForPayments', { defaultValue: 'This account is not eligible for online payments.' }));
				} else {
					this.validationErrors.push(this.i18n.i18next.t('customerAccountNotEligibleForPaymentsWithPhoneNumber', { defaultValue: `This account is not eligible for online payments, please contact us at ${this.globalState.business.portalSettings.csPhone}.`, phoneNumber: this.globalState.business.portalSettings.csPhone }));
				}
			}

			if (response.message === 'accountIsNoLongerActive') {
				this.validationErrors.push(this.i18n.i18next.t('accountIsNoLongerActive', { defaultValue: 'Your account is no longer active. If you feel you have received this message in error, please contact customer service.' }));
			} else if (!this.globalState.business.portalSettings.csPhone) {
				this.validationErrors.push(this.i18n.i18next.t('customerAccountNotFoundError', { defaultValue: 'Account cannot be located. Please contact customer service.' }));
			} else {
				this.validationErrors.push(this.i18n.i18next.t('customerAccountNotFoundErrorWithPhoneNumber', { defaultValue: `Account cannot be located. Please contact customer service at ${this.globalState.business.portalSettings.csPhone}`, phoneNumber: this.globalState.business.portalSettings.csPhone }));
			}

			return false;
		}

		if (this.newCustomer.isRegistered) {
			this.validationErrors.push(this.i18n.i18next.t('accountAlreadyRegistered', 'This account is already registered'));
			return false;
		} else {
			this.newCustomer.mobilePhone = ValidationHelpers.sanitizePhoneNumber(this.newCustomer.mobilePhone);
			let hasPortfolio = false;
			let portfolioIsConfigured = false;
			if (this.globalState.business.portalSettings.accountNumberSettings[0].required) {
				let account = this.newCustomer.customerAccounts.find((x) => {
					// Property managment ONLY. They can only have 1 merchant.
					// Validate the account number part of the referenceId against user entered
					// value.ReferenceId = [accountNumber - ownerKey]
					let index = x.referenceId.indexOf('-');
					if (index > 0 && this.globalState.business.verticalMarket === 2) {
						return x.referenceId.substring(0, index) === this.valueAccountNumber;
					}
					else {
						return x.referenceId.toUpperCase() === this.valueAccountNumber.toUpperCase();
					}
				});

				let portfolioName = account ? account.portfolioName : '';
				let portfolio = this.newCustomer.portfolios.find(x => {
					return x.portfolioName === portfolioName;
				});
				hasPortfolio = !!portfolio;
				if (hasPortfolio) {
					let p = new Portfolio(portfolio);
					portfolioIsConfigured = p.hasSettings;
				}
			} else {
				this.newCustomer.portfolios.forEach(portfolio => {
					let p = new Portfolio(portfolio);
					hasPortfolio = hasPortfolio || p.hasSettings;
					portfolioIsConfigured = p.hasSettings;
				});
			}

			if (hasPortfolio) {
				if (!portfolioIsConfigured) {
					if (!this.globalState.business.portalSettings.csPhone) {
						this.validationErrors.push(this.i18n.i18next.t('portalConfigurationErrorContactCustomerService', 'Portal Configuration Error: The portfolio for this account has not been setup. Please contact customer service.'));
					} else {
						this.validationErrors.push(this.i18n.i18next.t('portalConfigurationErrorContactCustomerServiceWithNumber', 'Portal Configuration Error: The portfolio for this account has not been setup. Please contact customer service at') + ' ' + this.globalState.business.portalSettings.csPhone + '.');
					}
					return false;
				}
			} else {
				this.validationErrors.push(this.i18n.i18next.t('customerAccountNotFoundError', { defaultValue: 'Account cannot be located. Please contact customer service.' }));
				return false;
			}
		}

		return this.validationErrors.length === 0;
	}

	handleErrors(reason: any) {
		if (reason.status === 404) {
			this.validationErrors.push(
				this.i18n.i18next.t(
					'yourAccountNoLongerActive',
					'Your account is no longer active. If you feel you have received this message in error, please contact customer service.'
				));
		} else {
			let baseMessage = this.i18n.i18next.t(
				'unableToRetrieveAccountInformation',
				'We are unable to retrieve your account information, please try again shortly.'
			);
			if (!this.globalState.business.portalSettings.csPhone) {
				this.validationErrors.push(baseMessage);
			} else {
				this.validationErrors.push(
					baseMessage +
					' ' +
					this.i18n.i18next.t(
						'ifIssuePersistsPleaseContact',
						'If the issue persists please contact us at') +
					' ' +
					this.globalState.business.portalSettings.csPhone +
					'.'
				);
			}
		}
	}

	cancel() {
		this.router.navigateToRoute('dashboard');
	}

	configureValidationRules() {
		ValidationRules

			.ensure('valueRegistrationId').displayName(this.labelRegistrationId)
			.required()

			.ensure('valueRegistrationId2').displayName(this.labelRegistrationId2)
			.required()

			.ensure('valueRegistrationId3').displayName(this.labelRegistrationId3)
			.required()

			.ensure('valueZipCode').displayName(this.labelZipCode)
			.required()

			.on(this);
	}
}
